import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { message } from 'antd';
import { isMobile } from 'react-device-detect';
import Layout from '../components/Layout/Layout';
import { apiClient } from '../config/api.client.js';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import RefreshIcon from '@mui/icons-material/Refresh';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import './css/OrderStatus.css';

const OrderStatus = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useSelector((state) => state.user);
  
  const [orderStatus, setOrderStatus] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [retryCount, setRetryCount] = useState(0);
  const [isRetrying, setIsRetrying] = useState(false);
  const fbqFiredRef = useRef(false);

  // Get order ID from URL params - UPI payments use client_txn_id
  const orderId = searchParams.get('client_txn_id') || 
                 searchParams.get('orderId') || 
                 searchParams.get('order_id') || 
                 searchParams.get('transactionId') ||
                 searchParams.get('id');

  useEffect(() => {
    if (orderId) {
      checkOrderStatus();
    } else {
      setError('No order ID provided');
      setIsLoading(false);
    }
  }, [orderId]);

  // Fire Meta Pixel purchase event once when order succeeds
  useEffect(() => {
    if (!orderStatus) return;
    const status = String(orderStatus.status || '').toLowerCase();
    if ((status === 'success' || status === 'completed') && !fbqFiredRef.current) {
      const amountNumber = typeof orderStatus.amount === 'number' ? orderStatus.amount : parseFloat(String(orderStatus.amount || '0').replace(/[^0-9.]/g, '')) || 0;
      if (typeof window !== 'undefined' && typeof window.fbq !== 'undefined') {
        try {
          // Use order ID as deduplication key
          const deduplicationId = orderStatus.orderId || orderId;
          window.fbq('track', 'Purchase', {
            value: amountNumber,
            currency: 'INR',
            payment_method: 'upi',
          }, {
            eventID: deduplicationId ? `order_${deduplicationId}` : undefined
          });
          fbqFiredRef.current = true;
        } catch (_) {
          // ignore pixel errors
        }
      }
    }
  }, [orderStatus]);

  const checkOrderStatus = async () => {
    try {
      setIsLoading(true);
      setError(null);
      
      const response = await apiClient.get(`order/order-status?orderId=${orderId}`, {
        headers: {
          Authorization: "Bearer " + localStorage.getItem("token"),
        },
      });
      
      if (response.data?.success && response.data?.order) {
        setOrderStatus(response.data.order);
        setError(null);
      } else {
        const errorMessage = response.data?.message || response.data?.error || 'Failed to fetch order status';
        
        if (errorMessage.toLowerCase().includes('order not found') || 
            errorMessage.toLowerCase().includes('not found')) {
          setError('ORDER_NOT_FOUND');
        } else {
          setError(errorMessage);
        }
      }
    } catch (err) {
      console.error('Error checking order status:', err);
      
      if (err.response?.status === 404) {
        setError('ORDER_NOT_FOUND');
      } else if (err.response?.data?.message) {
        setError(err.response.data.message);
      } else {
        setError('Failed to check order status');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleRetry = async () => {
    if (retryCount >= 3) {
      message.error('Maximum retry attempts reached');
      return;
    }
    
    setIsRetrying(true);
    setRetryCount(prev => prev + 1);
    
    // Wait 2 seconds before retrying
    setTimeout(() => {
      checkOrderStatus();
      setIsRetrying(false);
    }, 2000);
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'success':
      case 'completed':
        return <CheckCircleIcon className="status-icon success" />;
      case 'failed':
      case 'cancelled':
        return <CancelIcon className="status-icon failed" />;
      case 'pending':
      case 'processing':
        return <HourglassEmptyIcon className="status-icon pending" />;
      default:
        return <HourglassEmptyIcon className="status-icon pending" />;
    }
  };

  const getStatusMessage = (status) => {
    switch (status) {
      case 'success':
      case 'completed':
        return {
          title: 'Order Successful!',
          message: 'Your order has been processed successfully.',
          color: 'success'
        };
      case 'failed':
      case 'cancelled':
        return {
          title: 'Order Failed',
          message: 'We encountered an issue while processing your order.',
          color: 'failed'
        };
      case 'pending':
      case 'processing':
        return {
          title: 'Order Processing',
          message: 'Your order is being processed. Please wait...',
          color: 'pending'
        };
      default:
        return {
          title: 'Order Status Unknown',
          message: 'Unable to determine order status.',
          color: 'pending'
        };
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleString('en-IN', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatAmount = (amount) => {
    return `₹${parseFloat(amount || 0).toFixed(2)}`;
  };

  // Parse description field to extract playerId and server
  const parseDescription = (description) => {
    if (!description) return { playerId: null, server: null };
    
    try {
      const parsed = JSON.parse(description);
      return {
        playerId: parsed.playerId || null,
        server: parsed.server || null
      };
    } catch (error) {
      console.warn('Failed to parse description:', error);
      return { playerId: null, server: null };
    }
  };

  if (isLoading) {
    return (
      <Layout>
        <div className="order-status-container">
          <div className="order-status-card">
            <div className="loading-container">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
              <h4>Checking Order Status...</h4>
              <p>Please wait while we fetch your order details.</p>
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  if (error === 'ORDER_NOT_FOUND') {
    return (
      <Layout>
        <div className="order-status-container">
          <div className="order-status-card">
            <div className="error-container">
              <CancelIcon className="status-icon failed" />
              <h4>Order Not Found</h4>
              <p>The order you're looking for doesn't exist or has been removed.</p>
              <div className="action-buttons">
                <button 
                  className="btn-primary" 
                  onClick={() => navigate('/')}
                >
                  <ArrowBackIcon /> Back to Home
                </button>
                <button 
                  className="btn-secondary" 
                  onClick={() => navigate('/orders')}
                >
                  View All Orders
                </button>
              </div>
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  if (error) {
    return (
      <Layout>
        <div className="order-status-container">
          <div className="order-status-card">
            <div className="error-container">
              <CancelIcon className="status-icon failed" />
              <h4>Error Loading Order</h4>
              <p>{error}</p>
              <div className="action-buttons">
                <button 
                  className="btn-primary" 
                  onClick={handleRetry}
                  disabled={isRetrying || retryCount >= 3}
                >
                  <RefreshIcon /> 
                  {isRetrying ? 'Retrying...' : 'Retry'}
                </button>
                <button 
                  className="btn-secondary" 
                  onClick={() => navigate('/')}
                >
                  <ArrowBackIcon /> Back to Home
                </button>
              </div>
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  if (!orderStatus) {
    return (
      <Layout>
        <div className="order-status-container">
          <div className="order-status-card">
            <div className="error-container">
              <CancelIcon className="status-icon failed" />
              <h4>No Order Data</h4>
              <p>Unable to load order information.</p>
              <div className="action-buttons">
                <button 
                  className="btn-primary" 
                  onClick={() => navigate('/')}
                >
                  <ArrowBackIcon /> Back to Home
                </button>
              </div>
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  const statusInfo = getStatusMessage(orderStatus.status);
  const { playerId, server } = parseDescription(orderStatus.description);

  return (
    <Layout>
      <div className="order-status-container">
        <div className="order-status-card">
          {
            console.log(orderStatus)
            
          }
          {/* Header */}
          <div className="order-status-header">
            {getStatusIcon(orderStatus.status)}
            <h2 className={`status-title ${statusInfo.color}`}>
              {statusInfo.title}
            </h2>
            <p className="status-message">{statusInfo.message}</p>
          </div>

          {/* Order Details */}
          <div className="order-details">
            <h3>Order Details</h3>
            <div className="details-grid">
              <div className="detail-item">
                <span className="label">Order ID:</span>
                <span className="value">{orderStatus.orderId || orderId}</span>
              </div>
              
              <div className="detail-item">
                <span className="label">Status:</span>
                <span className={`value status-badge ${statusInfo.color}`}>
                  {orderStatus.status?.toUpperCase()}
                </span>
              </div>
              
              <div className="detail-item">
                <span className="label">Amount:</span>
                <span className="value amount">{formatAmount(orderStatus.amount)}</span>
              </div>
              
              <div className="detail-item">
                <span className="label">Payment Method:</span>
                <span className="value">{orderStatus.paymentMethod || 'N/A'}</span>
              </div>
              
              
              <div className="detail-item">
                <span className="label">Player ID:</span>
                <span className="value">{playerId || orderStatus.playerId || 'N/A'}</span>
              </div>
              
              {(server || orderStatus.server) && (
                <div className="detail-item">
                  <span className="label">Server:</span>
                  <span className="value">{server || orderStatus.server}</span>
                </div>
              )}
              
              <div className="detail-item">
                <span className="label">Order Date:</span>
                <span className="value">{formatDate(orderStatus.createdAt)}</span>
              </div>
              
              {orderStatus.completedAt && (
                <div className="detail-item">
                  <span className="label">Completed:</span>
                  <span className="value">{formatDate(orderStatus.completedAt)}</span>
                </div>
              )}
            </div>
          </div>

          {/* Action Buttons */}
          <div className="action-buttons">
            {orderStatus.status === 'pending' || orderStatus.status === 'processing' ? (
              <button 
                className="btn-primary" 
                onClick={handleRetry}
                disabled={isRetrying || retryCount >= 3}
              >
                <RefreshIcon /> 
                {isRetrying ? 'Refreshing...' : 'Refresh Status'}
              </button>
            ) : null}
            
            <button 
              className="btn-secondary" 
              onClick={() => navigate('/')}
            >
              <ArrowBackIcon /> Back to Home
            </button>
            
            <button 
              className="btn-outline" 
              onClick={() => navigate(isMobile ? '/reports?type=purchase' : '/orders')}
            >
              View Order History
            </button>
            
            {orderStatus.status === 'failed' && (
              <button 
                className="btn-danger" 
                onClick={() => navigate('/support')}
              >
                Contact Support
              </button>
            )}
          </div>

          {/* Additional Info */}
          {orderStatus.status === 'pending' && (
            <div className="pending-info">
              <p>
                <strong>Note:</strong> Orders typically take 5-15 minutes to process. 
                If your order is still pending after 30 minutes, please contact support.
              </p>
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default OrderStatus;